import React, { useState, useEffect } from "react";
import { Link, Redirect } from "react-router-dom";

import useApi, {
  fetcher,
} from "@action.sustainability/storybook-dashboard/utils/fetching";
import { BasicSpinner } from "@action.sustainability/storybook-dashboard/spinners/basicSpinner";
import Im from "immutable";
import Octicon from "react-octicon";
import { FormGroup, postFetch } from "../utils/forms";
import useToken from "../auth/token";
import { Modal, useModal } from "../modal";
import { SetupCompanyModal, SetupCompanyForm } from "./setup";
import CompanyInviteRow from "./inviteItem";


const createCompany = ({ e, setPending, onSuccess }) => {
  e.preventDefault();
  setPending(true);
  const formData = new FormData(e.target);
  const data = Object.fromEntries(formData.entries());
  console.log("createCompany", data);
  postFetch(`/api/company/`, data).then((data) => {
    console.log("Setup company succeeded", data);
    onSuccess();
  });
};

function CreateCompanyButton({ hide }) {
  const { setModal } = useModal();
  if (hide) {
    return null;
  }
  return (
    <button
      className="btn btn-sm btn-primary float-right"
      onClick={() => setModal(<SetupCompanyModal />)}
    >
      Create a company
      <Octicon className="ml-2 pt-1" name="plus" />
    </button>
  );
}

const setClient = ({ isClient, companyId, onSuccess }) => {
  fetcher(`/api/client/${companyId}/`, "PATCH", { is_client: !isClient }).then(
    (data) => {
      console.log("Change client status", data);
      onSuccess();
    },
  );
};

function MakeClientConfirmModal({isClient, company, onSuccess}) {
  let { hideModal }  = useModal()
  const companyId = company.get("uid");
  const name = company.get("name")
  let text = isClient ? (
    <p>Company {name} will not longer be listed as a Client.  They can no longer receive shared 
    assets however assets already shared with them will remain.</p>
  ) : (
    <p>Company {name} will be listed as a client and can receive shared assets.</p>
  )
  return (
    <Modal title="Confirm">
      {text}
      <button 
        className="btn btn-outline-info"
        onClick={() => setClient({ isClient, companyId, onSuccess })}
      >
        Confirm
      </button>
      <button 
        className="btn btn-outline-danger float-right"
        onClick={() => hideModal()}
      >
        Cancel
      </button>
    </Modal>
  )
}

function MakeClientButton({ company, hide }) {
  let { setModal, hideModal }  = useModal()
  const { trigger } = useApi(`/api/client/`);
  const onSuccess = () => {
    trigger();
    hideModal()
  };

  if (hide) return null;

  const companyId = company.get("uid");
  const isClient = company.get("isClient", false);

  return (
    <span
      style={{ cursor: "pointer" }}
      className={`badge badge-${isClient ? "warning" : "info"} float-right`}
      onClick={(e) => setModal((<MakeClientConfirmModal {...{isClient, company, onSuccess}}/>))}
    >
      {isClient ? "Client" : "Supplier"}
    </span>
  );
}

function PendingInvitesList() {
  const { data: invites, isLoading } = useApi(`/api/company/invite/`);
  if (!invites?.size) {
    return null;
  }

  return (
    <>
      <h4 className="mb-3">Pending confirmation</h4>
      <p>
        There are pending actions associated with your profile. 
        You may have either requested to join a company account 
        or received one or more invitations from different 
        company accounts.
      </p>
      <p>
        If you are awaiting approval for your request to join, 
        no immediate action is required; however, should you 
        need to resend a request or adjust any invitations, you
        have the option to do so.
      </p>
      <p>
        If you have been invited to join another account, you can
        choose to accept the invitation and join their team, or 
        reject it if it does not apply to you
      </p>
      <p>
        Note: email addresses will remain confidential in
        accordance with GDPR until your involvement with the
        company is confirmed.
      </p>
      <p>Please review the list below and take the appropriate action:</p>
      <table className="table table-sm table-striped">
        <tbody>
          {invites?.map((invite, i) => (
            <CompanyInviteRow key={i} invite={invite} />
          ))}
        </tbody>
      </table>
    </>
  );
}

function UserCompanyList({ is_superuser, companies }) {
  if (!companies?.size) {
    return null;
  }

  return (
    <>
      <h3>
        My Companies
        <CreateCompanyButton hide={!is_superuser} />
      </h3>
      <table className="table mt-3">
        <tbody>
          {companies?.map((company, i) => {
            const companyId = company.get("uid");
            return (
              <tr key={i}>
                <td>
                  <Link to={`/company/${companyId}/catalog`}>
                    {company.get("name")}
                  </Link>
                  <Link to={`/company/${companyId}`}>
                    <Octicon className="float-right ml-2" name="gear" />
                  </Link>
                  <MakeClientButton hide={!is_superuser} company={company} />
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </>
  );
}

function ErrorLoadingDataAlert({trigger}) {
  return (
    <div className="alert alert-warning" role="alert">
      <p>Error loading data</p>
      <button 
        className="btn btn-outline-primary btn-sm"
        onClick={() => trigger()}
      >
        Retry
      </button>
    </div>
  )
}

export function CompanyList(props) {
  // Get a list of the companies that the user belongs to
  let { data: companies, isLoading, trigger } = useApi(`/api/company/`);
  const { data: invites, isLoading: isLoadingInvites } =
    useApi(`/api/company/invite/`);
  const { data: clients, isLoading: isLoadingClients } = useApi(`/api/client/`);

  // Get superuser info from token
  const { token, decodeToken } = useToken();
  const [claims, setClaims] = useState(decodeToken(token));
  const is_superuser = claims.is_superuser || claims.is_tenant_superuser;

  if (isLoading || isLoadingClients || isLoadingInvites) {
    return <BasicSpinner />;
  }

  // Error loading data if no company data
  if (!companies) {
    return (<ErrorLoadingDataAlert {...{trigger}}/>)
  }

  // User has no companies so set them up
  if (!companies?.size && !invites?.size) {
    return <SetupCompanyForm onSuccess={() => trigger()} />;
  }

  // If the user is not a superuser and has only one company - redirect them through to that page
  if (!is_superuser && companies?.size == 1) {
    const companyId = companies.first().get("uid");
    return <Redirect to={`/company/${companyId}/catalog`} />;
  }

  // Get a set of the client ids - Reorder list to clients-first
  const clientIds = Im.Set((clients || Im.List()).map((i) => i.get("uid")));
  companies = companies
    .map((i) => i.set("isClient", clientIds.has(i.get("uid"))))
    .sortBy((i) => `${i.get("isClient") ? "C" : "S"}-${i.get("name")}`);
  console.log(
    "CompanyList",
    is_superuser,
    clientIds?.toJS(),
    companies?.toJS(),
  );

  // User has multiple companies for whatever reason (superuser, etc.)  Let them choose from a list.
  return (
    <>
      <PendingInvitesList />
      <UserCompanyList is_superuser={is_superuser} companies={companies} />
    </>
  );
}
